import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { createRepository } from '../../../dto/repositoryFactory';
import {
  ApiResponse,
  MN_CUSTOMERS,
  MN_JOBS,
  MN_RESOURCES,
  MN_SUPPLIERS,
} from '../../../common';
import {
  ICustomer,
  IJob,
  IListCustomerFetchParams,
  IListJobFetchParams,
  IListSupplierFetchParams,
  IResource,
  IResourceFetchParams,
  ISupplier,
} from '../../../dto';
import { AxiosError } from 'axios';
import { handleAxiosError } from '../../../utils';

const HookComponentOrder: React.FC = () => {
  const { setValue, watch, formState } = useFormContext();
  const supplierId = watch('supplierId');
  const orderType = watch('orderType');
  const resourceId = watch('resourceId');
  const jobId = watch('jobId');

  const handleResetNotLaborFields = () => {
    setValue('fixedCost', null);
  };

  const handleResetNotMaterialFields = () => {
    setValue('resourceId', null);
  };

  const handleChangeResourceId = async () => {
    if (resourceId === null || resourceId === undefined) {
      handleResetResourceId();
      return;
    }

    if (formState.dirtyFields['resourceId']) {
      handleResetResourceId();
      const repository = createRepository<IResource, IResourceFetchParams>(
        MN_RESOURCES,
      );
      repository
        .get(resourceId)
        .then((response: ApiResponse<IResource>) => {
          if (response?.data) {
            const resource = response?.data;
            setValue('hourCost', resource?.hourCost ?? null);
            setValue('hourRevenue', resource?.hourRevenue ?? null);
            setValue('hoursAllocatedPerDay', 8);
          }
        })
        .catch((err: AxiosError) => handleAxiosError(err));
    }
  };

  const handleResetResourceId = () => {
    setValue('hoursAllocatedPerDay', null);
    setValue('hourCost', null);
    setValue('hourRevenue', null);
    setValue('dailyCost', null);
    setValue('dailyRevenue', null);
  };

  const handleChangeSupplierId = () => {
    if (supplierId === null || supplierId === undefined) {
      setValue('typeOfPaymentSupplierId', null);
      return;
    }
    if (formState.dirtyFields['supplierId']) {
      const repository = createRepository<ISupplier, IListSupplierFetchParams>(
        MN_SUPPLIERS,
      );
      repository
        .get(supplierId)
        .then((response: ApiResponse<ISupplier>) => {
          if (response?.data) {
            const supplier = response?.data;
            setValue(
              'typeOfPaymentSupplierId',
              supplier?.typeOfPayment?.id ?? null,
            );
          }
        })
        .catch((err: AxiosError) => handleAxiosError(err));
    }
  };

  const handleChangeJobId = async () => {
    if (jobId === null || jobId === undefined) {
      setValue('typeOfPaymentId', null);
      return;
    }
    if (formState.dirtyFields['jobId']) {
      const repository = createRepository<IJob, IListJobFetchParams>(MN_JOBS);
      const customerId = await repository
        .get(jobId)
        .then((response: ApiResponse<IJob>) => {
          if (response?.data) {
            const job = response?.data;
            return job?.customerId;
          }
        })
        .catch((err: AxiosError) => handleAxiosError(err));

      if (customerId) {
        const repository = createRepository<
          ICustomer,
          IListCustomerFetchParams
        >(MN_CUSTOMERS);
        repository
          .get(customerId)
          .then((response: ApiResponse<ICustomer>) => {
            if (response?.data) {
              const customer = response?.data;
              setValue('typeOfPaymentId', customer?.typeOfPayment?.id ?? null);
            }
          })
          .catch((err: AxiosError) => handleAxiosError(err));
      }
    }
  };

  useEffect(() => {
    handleChangeSupplierId();
  }, [supplierId]);

  useEffect(() => {
    if (orderType === 'Labor') {
      handleResetNotLaborFields();
    }
    if (orderType === 'Material') {
      handleResetNotMaterialFields();
    }
  }, [orderType]);

  useEffect(() => {
    handleChangeResourceId().then();
  }, [resourceId]);

  useEffect(() => {
    handleChangeJobId().then();
  }, [jobId]);

  return <div />;
};

export default HookComponentOrder;
